<template>
	<div class="menu-editor" ref="menu-editor">
		<div class="categories-container" :class="{'no-margin': variationsOnly}" ref="categories-list">
			<div class="category" v-for="(category) in sortedCategories" :key="category.tempid"
				 :data-tempid="category.tempid" :style="'--category-color: '+category.color+';--category-num-color: '+categoryNumColor(category.color)+';--category-text-color: '+categoryTextColor(category.color)">
				<div class="category-container">
					<div class="category-header-container">
						<div :ref="'category-header-'+category.tempid" class="category-header" :data-tempid="category.tempid" :class="{droppable: category.droppable, droppableHover: category.droppableHover}"
							@mouseenter="mouseEnterCategory(category)" @mouseup="mouseUpCategory(category)" @mouseleave="mouseLeaveCategory(category)">
							<div class="handle" v-if="!multiSelectMode" :class="{undraggable: !category.collapsed}"><img class="icon-dimension-20" :src="'https://img.qromo.io/img/vue/icons/icon-sort-list-item'+(userStore.darkMode ? '-dark' : '')+'.svg'" alt="handle"></div>
							<div class="multi-select-dot" :class="{transparent: !category.items || !Object.keys(category.items).length}" v-if="multiSelectMode">
								<AppCheckbox @click="clickCategoryMultiSelect(category.tempid)" :is-active="selectedCategory(category.tempid)" :auto-toggle="false" />
							</div>
							<div class="category-header-content" :class="{multiSelectMode}">
								<div class="category-name" @click="categoryExpandClicked(category.tempid)">
									<h3>{{ category.name }}</h3>
									<div class="category-items-num" :class="{'no-items': Object.values(category?.items || {}).length === 0}">
										<span v-if="Object.values(category?.items || {}).length > 0">{{ Object.values(category?.items || {}).length }}</span>
									</div>
									<div class="category-items-selected-num" :class="{active: !!selectedQuantityCategory(category.tempid)}">
										<span>{{ selectedQuantityCategory(category.tempid) }}</span>
									</div>
								</div>
								<div class="category-actions">
									<AppButtonIcon :ref="'edit-category-'+category.tempid" :active="category.editActive" icon-name="icon-edit-white"
												   small @click="categoryEditExpandClicked(category.tempid)" v-if="!multiSelectMode"/>
									<AvailabilityDot :class="{visible: category.active === false || category.active === null}" :type="availabilityType(category)" />
								</div>
							</div>
							<div class="dropdown-arrow" @click="categoryExpandClicked(category.tempid)">
								<AppChevronIcon :direction="category.collapsedDelayed ? 'down' : 'up'"/>
							</div>
						</div>
						<AppDropdown square-corner :collapsed="!category.editActiveDelayed">
							<div class="category-edit-container" v-if="category.editActive">
								<CategoryEditor :ref="'category-editor-'+category.tempid" :categoryTempId="category.tempid" @category-saved="saveCategory(category)"/>
							</div>
						</AppDropdown>
					</div>
					<AppDropdown square-corner v-if="category.tempid" white :collapsed="category.collapsedDelayed">
						<MenuEditorItems :variations-only="variationsOnly" :ref="'items-editor-'+category.tempid" :categoryTempId="category.tempid" @item-saved="(item) => $emit('item-saved', item)" @sorting="(item) => startedSorting(category, item)" @sorted="stoppedSorting" @category-saved="$emit('category-saved')" />
					</AppDropdown>
				</div>
			</div>

			<div class="category category-orphans" :style="'--category-color: #cecece;'" v-if="!variationsOnly && Object.values(menuStore.modMenu.orphanItems).length > 0">
				<div class="category-container">
					<div class="category-header-container">
						<div class="category-header">
							<div v-if="!multiSelectMode" class="handle-placeholder"></div>
							<div class="multi-select-dot" :class="{transparent: !Object.values(menuStore.modMenu.orphanItems).length}" v-if="multiSelectMode">
								<AppCheckbox @click="clickCategoryMultiSelect(null)" :is-active="selectedCategory(null)" :auto-toggle="false" />
							</div>
							<div class="category-header-content">
								<div class="category-name" @click="categoryExpandClicked(null)">
									<h3>{{ getString('without_category') }}</h3>
									<div class="category-items-num" :class="{'no-items': Object.values(menuStore.modMenu.orphanItems).length === 0}">
										<span v-if="Object.values(menuStore.modMenu.orphanItems).length > 0">{{ Object.values(menuStore.modMenu.orphanItems).length }}</span>
									</div>
									<div class="category-items-selected-num" :class="{active: !!selectedQuantityCategory(null)}">
										<span>{{ selectedQuantityCategory(null) }}</span>
									</div>
								</div>
								<div class="category-actions-placeholder"></div>
							</div>
							<div class="dropdown-arrow" @click="categoryExpandClicked(null)">
								<AppChevronIcon :direction="orphanCollapsedDelayed ? 'down' : 'up'"/>
							</div>
						</div>
					</div>
					<AppDropdown white :collapsed="orphanCollapsedDelayed">
						<MenuEditorItems ref="orphans-editor" :categoryTempId="null" @item-saved="(item) => $emit('item-saved', item)" @sorting="(item) => startedSorting(null, item)" @sorted="stoppedSorting" />
					</AppDropdown>
				</div>
			</div>

			<div v-if="!multiSelectMode && !variationsOnly" class="category category-add">
				<div class="list-item list-item-add category-header" @click="addCategory">
					<img :src="'https://img.qromo.io/img/vue/icons/icon-plus'+(userStore.darkMode ? '-dark' : '')+'.svg'" draggable="false" class="icon-dimension-15 mr-10" alt="icon plus">
					<p class="list-item-title">{{ getString('add_category') }}</p>
				</div>
			</div>
		</div>
		<AppBottomSheet v-if="!variationsOnly" :z-index="999" undismissable @click-overlay="clickMultiSelectOverlay" :force-width="bottomSheetWidth" :no-overlay="minimizedMultiSelectSheet" :active="multiSelectMode">
			<MultiSelectEditor @click-modal-cta="clickMultiSelectCta" :active="!minimizedMultiSelectSheet" v-if="multiSelectMode" />
		</AppBottomSheet>
	</div>
</template>

<script>
import {useLanguageStore, useScriptStore} from "@/shared/stores";
import {useMenuStore, useUserStore} from "@/console-new/stores";

import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";
import AppCheckbox from "@/shared/components/AppCheckbox.vue";
import CategoryEditor from "@/console-new/components/config/menu/CategoryEditor.vue";
import MenuEditorItems from "@/console-new/components/config/menu/MenuEditorItems.vue";
import AvailabilityDot from "@/console-new/components/config/menu/AvailabilityDot.vue";
import MultiSelectEditor from "@/console-new/components/config/menu/MultiSelectEditor.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";

import {isTextColorDark} from "@/shared/helpers/ui";

export default {
	emits: ["item-saved", "category-saved"],
	setup() {
		let languageStore = useLanguageStore();
		let menuStore = useMenuStore();
		let scriptStore = useScriptStore();
		let userStore = useUserStore();
		return {getString: languageStore.getString, menuStore, loadScript: scriptStore.loadScript, userStore};
	},
	mixins: [WindowWidth],
	inject: ["providedAction"],
	name: "MenuEditor",
	components: {AvailabilityDot, MenuEditorItems, CategoryEditor, AppDropdown, AppCheckbox, AppButtonIcon, AppChevronIcon, AppBottomSheet, MultiSelectEditor},
	props: {
		variationsOnly: {
			type: Boolean,
			required: false,
			default: false
		}
	},
	data() {
		return {
			sortable: null,
			draggingItem: null,
			draggingCategory: null,
			scrollCategoryTimeout: null,
			orphanCollapsed: true,
			orphanCollapsedDelayed: true,
			multiSelectMode: false,
			minimizedMultiSelectSheet: true,
			doingFunnelProductsTask: false,
		}
	},
	computed: {
		sortedCategories() {
			let filterFunction = this.variationsOnly ? (cat) => !!cat.variations_only : (cat) => !cat.variations_only;
			return  Object.values(this.menuStore.modMenu.categories).filter(filterFunction).sort((a, b) => a.order_number - b.order_number);
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		selectedItems() {
			return this.menuStore.selectedItems().length;
		},
	},
	methods: {
		categoryTextColor(color) {
			if(isTextColorDark(color)) {
				return "#000000";
			} else {
				return "#ffffff";
			}
		},
		categoryNumColor(color) {
			if(isTextColorDark(color)) {
				return "#000000";
			} else {
				return color;
			}
		},
		initSortable() {
			let self = this;
			if(this.$refs["categories-list"]) {
				this.sortable = new Sortable(this.$refs["categories-list"], {
					handle: '.handle',
					animation: 250,
					dataIdAttr: 'data-tempid',
					filter: '.category-add',
					onStart: function (e) {
						let categoryTempId = e.item.getAttribute('data-tempid');
						let category = self.menuStore.modMenu.categories[categoryTempId];
						if(category.editActive) {
							self.categoryEditExpandClicked(categoryTempId);
						}
					},
					onEnd: evt => {
						let sortedItems = self.sortable.toArray();
						for(let i = 0; i < sortedItems.length; i++) {
							let categoryTempId = sortedItems[i];
							if(self.menuStore.modMenu.categories.hasOwnProperty(categoryTempId)) {
								self.menuStore.modMenu.categories[categoryTempId].order_number = i;
							}
						}
					},
					onMove: evt => evt.related.className.indexOf('category-add') === -1,
				});
			}
		},
		expandedCategoryDropdownHeight(categoryTempId) {
			let finalHeight = 0;
			let categoryElement = document.querySelector('.category[data-tempid="' + categoryTempId + '"]');
			finalHeight += categoryElement.offsetHeight;
			finalHeight -= categoryElement.querySelector('.category-header').offsetHeight;
			return finalHeight;
		},
		categoryEditExpandClicked(categoryTempid) {
			let openHeight = 0;
			let currentCategoryPosition = this.sortedCategories.indexOf(this.menuStore.modMenu.categories[categoryTempid]);
			let self = this;
			Object.entries(this.menuStore.modMenu.categories).forEach(([i, category]) => {
				if(this.$refs['items-editor-'+category.tempid] && this.$refs['items-editor-'+category.tempid]?.[0]) {
					this.$refs['items-editor-'+category.tempid][0].closeAllItems();
				}
				let aboveCategory = self.sortedCategories.indexOf(category) < currentCategoryPosition;
				if((!category.collapsed || category.editActive) && aboveCategory) {
					openHeight += self.expandedCategoryDropdownHeight(category.tempid);
				}
				if (category.tempid === categoryTempid) {
					if (!category.editActive) {
						category.editActive = true;
						let buttonIcon = self.$refs["edit-category-" + category.tempid]?.[0];
						if(buttonIcon) {
							buttonIcon.isActive = true;
						}

						requestAnimationFrame(() => {
							requestAnimationFrame(() => {
								category.editActiveDelayed = category.editActive;
								if(category.editActive) {
									self.scrollCategoryToTop(categoryTempid, openHeight);
								}
							});
						});

						if(!category.collapsed) {
							self.categoryExpandClicked(categoryTempid);
						}
					} else if (category.editActiveDelayed) {
						category.editActiveDelayed = false;
						setTimeout(() => {
							category.editActive = category.editActiveDelayed;
							let buttonIcon = self.$refs["edit-category-" + category.tempid]?.[0];
							if(buttonIcon) {
								buttonIcon.isActive = true;
							}
							if(category.editActive) {
								self.scrollCategoryToTop(categoryTempid, openHeight);
							}
						}, 520);
					}
				} else {
					if (category.editActiveDelayed) {
						category.editActiveDelayed = false;
						setTimeout(() => {
							category.editActive = category.editActiveDelayed;
							if(self.$refs["edit-category-" + category.tempid]?.[0]) {
								self.$refs["edit-category-" + category.tempid][0].isActive = category.editActive;
							}
						}, 520);
					}
					if(!category.collapsedDelayed) {
						category.collapsedDelayed = true;
						setTimeout(() => {
							category.collapsed = category.collapsedDelayed;
						}, 520);
					}
				}
			});

		},
		categoryExpandClicked(categoryTempid) {

			let self = this;
			let openHeight = 0;
			let targetCategory = this.menuStore.modMenu.categories[categoryTempid];
			let currentCategoryPosition = this.sortedCategories.indexOf(targetCategory);

			Object.entries(this.sortedCategories).forEach(([i, category]) => {
				if(this.$refs['items-editor-'+category.tempid] && this.$refs['items-editor-'+category.tempid][0]) {
					this.$refs['items-editor-'+category.tempid][0].closeAllItems();
				}
				let aboveCategory = self.sortedCategories.indexOf(category) < currentCategoryPosition || categoryTempid === null;
				if((!category.collapsed || category.editActive) && aboveCategory) {
					openHeight += self.expandedCategoryDropdownHeight(category.tempid);
				}
				if (category.tempid === categoryTempid) {
					if (category.collapsed) {
						category.collapsed = false;
						requestAnimationFrame(() => {
							requestAnimationFrame(() => {
								category.collapsedDelayed = category.collapsed;
								if(!category.collapsed) {
									self.scrollCategoryToTop(categoryTempid, openHeight);
								}
							});
						});

						if(category.editActive) {
							self.categoryEditExpandClicked(categoryTempid);
						}
					} else if (!category.collapsedDelayed) {
						category.collapsedDelayed = true;
						setTimeout(() => {
							category.collapsed = category.collapsedDelayed;
							if(!category.collapsed) {
								self.scrollCategoryToTop(categoryTempid, openHeight);
							}
						}, 520);
					}
				} else {
					if (!category.collapsedDelayed) {
						category.collapsedDelayed = true;
						setTimeout(() => {
							category.collapsed = category.collapsedDelayed;
						}, 520);
					}
					if(category.editActiveDelayed) {
						category.editActiveDelayed = false;
						setTimeout(() => {
							category.editActive = category.editActiveDelayed;
							if(self.$refs["edit-category-" + category.tempid]?.[0]) {
								self.$refs["edit-category-" + category.tempid][0].isActive = category.editActive;
							}
						}, 520);
					}
				}
			});
			if(categoryTempid === null) {
				if(this.orphanCollapsed) {
					this.orphanCollapsed = false;
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							self.orphanCollapsedDelayed = self.orphanCollapsed;
							if(!self.orphanCollapsed && self.sortedCategories.length) {
								let targetCategoryId = self.sortedCategories[self.sortedCategories.length - 1].tempid;
								self.scrollCategoryToTop(targetCategoryId, openHeight - 35);
							}
						});
					});
				} else if (!this.orphanCollapsedDelayed) {
					self.orphanCollapsedDelayed = true;
					setTimeout(() => {
						self.orphanCollapsed = self.orphanCollapsedDelayed;
					}, 520);
				}
			} else {
				if (!this.orphanCollapsedDelayed) {
					self.orphanCollapsedDelayed = true;
					setTimeout(() => {
						self.orphanCollapsed = self.orphanCollapsedDelayed;
					}, 520);
				}
			}

		},
		scrollCategoryToTop(catTempId, openCategoryHeight = 0) {
			let categoryElement = this.$refs["category-header-"+catTempId]?.[0];
			if(!categoryElement) { return; }
			let mainContainer = document.querySelector('.main-content');
			let mainContainerPaddingStyle = window.getComputedStyle(mainContainer, null).getPropertyValue('padding-top');
			let mainContainerPadding = parseInt(mainContainerPaddingStyle) || 0;
			let cardPadding = window.innerWidth <= 768 ? 18 : 24;
			let categoryOffsetTop = categoryElement && categoryElement.offsetTop ? categoryElement.offsetTop : 0;
			let targetScroll = categoryOffsetTop + mainContainerPadding + cardPadding - openCategoryHeight - 1;

			if(targetScroll < 80) {
				return;
			}

			mainContainer.scrollTo({
				top: targetScroll,
				behavior: 'smooth'
			});

			clearTimeout(this.scrollCategoryTimeout);
			this.scrollCategoryTimeout = setTimeout(()=>{
				if(mainContainer.scrollTop !== targetScroll) {
					mainContainer.scrollTo({
						top: targetScroll,
						behavior: 'smooth'
					});
				}
			}, 550);
		},
		closeAllCategories(exceptTempId = null) {
			let self = this;
			Object.entries(this.menuStore.modMenu.categories).forEach(([i, category]) => {
				if(this.$refs['items-editor-'+category.tempid] && this.$refs['items-editor-'+category.tempid][0]) {
					this.$refs['items-editor-'+category.tempid][0].closeAllItems();
				}
				if(!category.collapsed && category.tempid !== exceptTempId) {
					self.categoryExpandClicked(category.tempid);
				}
				if(category.editActive) {
					self.categoryEditExpandClicked(category.tempid);
				}
			});
			if (!this.orphanCollapsedDelayed) {
				self.orphanCollapsedDelayed = true;
				if(this.$refs['orphans-editor']) {
					this.$refs['orphans-editor'].closeAllItems();
				}
				setTimeout(() => {
					self.orphanCollapsed = self.orphanCollapsedDelayed;
				}, 520);
			}
		},
		closeAllCategoriesEdit() {
			let self = this;
			Object.entries(this.menuStore.modMenu.categories).forEach(([i, category]) => {
				if(category.editActive) {
					self.categoryEditExpandClicked(category.tempid);
				}
			});
		},
		saveCategory(category) {
			this.$emit('category-saved');
			if(this.doingFunnelProductsTask) {
				this.doingFunnelProductsTask = false;
				this.categoryExpandClicked(category.tempid);
				setTimeout(() => {
					if(this.$refs['items-editor-'+category.tempid]?.[0]?.addItem) {
						this.$refs['items-editor-'+category.tempid][0].addItem();
					}
				}, 200);
			} else {
				this.categoryEditExpandClicked(category.tempid);
			}
		},
		addCategory() {
			//Close other categories
			let self = this;
			let openCategoryHeight = 0;
			Object.values(self.menuStore.modMenu.categories).forEach((category) => {
				if(category.editActive || !category.collapsed) {
					openCategoryHeight += self.expandedCategoryDropdownHeight(category.tempid);
				}
				if(category.editActive) {
					self.categoryEditExpandClicked(category.tempid);
				}
				if(!category.collapsed) {
					self.categoryExpandClicked(category.tempid);
				}
			});
			if(!this.orphanCollapsed) {
				this.categoryExpandClicked(null);
			}

			let prefillFields = {};
			let newCategory = this.menuStore.addNewCategory(prefillFields);

			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					if(self.$refs["category-editor-" + newCategory.tempid]?.[0]?.$refs?.["name-input"]?.$refs?.["input"]) {
						self.$refs["category-editor-" + newCategory.tempid]?.[0].$refs["name-input"].$refs["input"].focus();
					}
					self.scrollCategoryToTop(newCategory.tempid, openCategoryHeight);
				});
			});

		},
		startedSorting(cat, item) {
			this.draggingItem = item;
			this.draggingCategory = cat;
			Object.values(this.menuStore.modMenu.categories).forEach((category) => {
				if(!cat || cat.tempid !== category.tempid) {
					category.droppable = true;
				}
			});
		},
		stoppedSorting() {
			this.draggingItem = null;
			this.draggingCategory = null;
			Object.values(this.menuStore.modMenu.categories).forEach((category) => {
				category.droppable = false;
				category.droppableHover = false;
			});
		},
		mouseEnterCategory(category) {
			if(!!this.draggingItem) {
				category.droppableHover = true;
			}
		},
		mouseLeaveCategory(category) {
			if(!!this.draggingItem) {
				category.droppableHover = false;
			}
		},
		mouseUpCategory(category) {
			if(!!this.draggingItem) {
				category.droppableHover = false;

				this.draggingItem.order_number = Math.max(...Object.values(category.items).map((item) => item.order_number)) + 1;
				if(this.draggingCategory && this.draggingItem.menu_category_id) {
					this.menuStore.modMenu.categories[this.draggingCategory.tempid].items[this.draggingItem.tempid].menu_category_id = category.menu_category_id;
					delete this.menuStore.modMenu.categories[this.draggingCategory.tempid].items[this.draggingItem.tempid];
				} else if(this.menuStore.modMenu.orphanItems[this.draggingItem.tempid]) {
					this.menuStore.modMenu.orphanItems[this.draggingItem.tempid].menu_category_id = category.menu_category_id;
					delete this.menuStore.modMenu.orphanItems[this.draggingItem.tempid];

					if(Object.values(this.menuStore.modMenu.orphanItems).length === 0) {
						Object.values(this.menuStore.modMenu.categories).forEach((c) => {
							c.droppable = false;
							c.droppableHover = false;
						});
					}
				} else {
					return;
				}
				let self = this;
				this.menuStore.modMenu.categories[category.tempid].items[this.draggingItem.tempid] = this.draggingItem;
				let modItem = this.menuStore.modMenu.categories[category.tempid].items[this.draggingItem.tempid];
				if(!this.menuStore.modMenu.categories[category.tempid].menu_category_id) {
					//save new category
					this.menuStore.saveCategories(function () {
						modItem.menu_category_id = self.menuStore.modMenu.categories[category.tempid].menu_category_id;
					})
				}

			}
		},
		availabilityType(category) {
			if(category.active === null) {
				return 2;
			} else {
				return 1;
			}
		},
		clickCategoryMultiSelect(tempId) {
			if(this.multiSelectMode) {
				let targetStatus = !this.selectedCategory(tempId);
				let items = tempId ? this.menuStore.modMenu.categories[tempId].items : this.menuStore.modMenu.orphanItems;
				for(let item in items) {
					items[item].selected = targetStatus;
				}
			}
		},
		selectedCategory(tempId) {
			let items = tempId ? this.menuStore.modMenu.categories[tempId].items : this.menuStore.modMenu.orphanItems;
			return !!(items && Object.keys(items).length && this.selectedQuantityCategory(tempId) && Object.keys(items).length === this.selectedQuantityCategory(tempId));
		},
		selectedQuantityCategory(tempId) {
			let selected = 0;
			let items = tempId ? this.menuStore.modMenu.categories[tempId].items : this.menuStore.modMenu.orphanItems;
			for(let item in items) {
				if(items[item].selected) {
					selected++;
				}
			}
			return selected;
		},
		clickMultiSelectCta(openCategoryTempId = null) {
			this.minimizedMultiSelectSheet = !this.minimizedMultiSelectSheet;
			this.closeAllCategories(openCategoryTempId);
			if(openCategoryTempId) {
				let finalCategoryId = openCategoryTempId === "orphan" ? null : openCategoryTempId;
				this.categoryExpandClicked(finalCategoryId);
			}
		},
		clickMultiSelectOverlay() {
			if(this.multiSelectMode && !this.minimizedMultiSelectSheet) {
				this.minimizedMultiSelectSheet = true;
			}
		},
	},
	mounted() {
		this.loadScript("sortable", () => {
			this.initSortable();
		});
		if(this.menuStore.shouldCreateCategory) {
			this.menuStore.shouldCreateCategory = false;
			this.doingFunnelProductsTask = true;
			if(!this.sortedCategories?.length) {
				this.addCategory();
			}
		}
	},
	watch: {
		"menuStore.shouldCreateCategory"(newVal, oldVal) {
			if (!!newVal && newVal !== oldVal) {
				this.menuStore.shouldCreateCategory = false;
				this.doingFunnelProductsTask = true;
				if(!this.sortedCategories?.length) {
					this.addCategory();
				}
			}
		},
		providedAction(newProvidedObject) {
			if(newProvidedObject.hasOwnProperty('multiSelectMode') && newProvidedObject.multiSelectMode !== this.multiSelectMode) {
				this.multiSelectMode = newProvidedObject.multiSelectMode;
				this.closeAllCategoriesEdit();
			}
		},
		selectedItems(newSelectedItems) {
			if(!newSelectedItems && !this.minimizedMultiSelectSheet) {
				this.minimizedMultiSelectSheet = true;
			}
		},
		sortedCategories(newCats, oldCats) {
			if(newCats && newCats.length === 1 && newCats[0].tempid && newCats[0].collapsed
				&& (!oldCats || !oldCats.length || oldCats[0].tempid !== newCats[0].tempid)) {
				if(newCats[0]?.name) {
					this.categoryExpandClicked(this.sortedCategories[0].tempid);
				} else if(!newCats[0]?.editActive) {
					this.categoryEditExpandClicked(this.sortedCategories[0].tempid);
				}
			}
		},
	}
}
</script>
<style lang="scss" scoped>
.icon-dimension-20 {
	width: 20px;
	height: 20px;
}
.menu-editor {
	.icon-dimension-15 {
		width: 15px;
		height: 15px;
	}

	.categories-container {
		border-top: 1px solid var(--color-border-3);
	}

	.categories-container {
		border-radius: 13px;
		margin-right: 20px;
		margin-left: 8px;
		box-shadow: 0 2px 2px var(--color-box-shadow-3);
		overflow: hidden;
		border-top: 1px solid var(--color-border-2);

		&.no-margin {
			margin-left: 0;
			margin-right: 0;
		}

		.category-container {
			width: 100%;

			.category-edit-container {
				padding: 0 20px;
			}

			.category-header-container {
				border-bottom: 1px solid var(--color-border-2);

				.category-header {
					display: flex;
					justify-content: space-between;
					align-items: stretch;
					transition: transform 0.3s ease;

					&.droppable {
						opacity: 0.25;

						&.droppableHover {
							opacity: 1;
							transform: scale(1.06);
						}

					}

					.handle {
						width: 30px;
						display: flex;
						justify-content: center;
						align-items: center;
						user-select: none;
						cursor: move;
						transition: opacity 0.3s ease;

						&.undraggable {
							cursor: default;
							pointer-events: none;
							opacity: 0.2;
						}
						img	{
							pointer-events: none;
						}
					}
					.multi-select-dot {
						width: 45px;

						&.transparent {
							opacity: 0;
							pointer-events: none;
						}
					}

					.category-header-content {
						width: calc(100% - 60px);
						display: flex;
						justify-content: space-between;
						align-items: center;

						&.multi-select-mode {
							width: calc(100% - 75px);
						}

						.category-name {
							@include noSelect;
							padding: 8px 33px 8px 10px;
							width: 50%;
							background-color: var(--category-color);
							position: relative;
							cursor: pointer;
							min-height: 36px;
							display: flex;
							align-items: center;
							transition: height 0.3s ease;

							h3 {
								font-size: 16px;
								color: var(--category-text-color);
								font-weight: normal;
								@include truncateString(1);
							}

							&:after {
								content: '';
								display: block;
								width: 40px;
								height: 100%;
								position: absolute;
								top: 0;
								right: -10px;
								background-color: var(--category-color);
								transform: skew(-15deg);
								border-radius: 5px;
							}

							.category-items-num {
								@include noSelect;
								width: 24px;
								height: 16px;
								position: absolute;
								top: calc(50% - 8px);
								right: 2px;
								transform: scale(1) skew(-15deg);
								display: flex;
								justify-content: center;
								align-items: center;
								box-shadow: inset 0 1px 2px $alpha_4;
								border-radius: 5px;
								background: var(--color-bg);
								z-index: 10;
								transition: transform 0.4s $transition;

								&.no-items {
									background-color: var(--category-color);
									box-shadow: unset;
									transform: scale(0) skew(-15deg);
								}

								span {
									font-size: 11px;
									color: var(--color-txt);
								}

								.items-num-container {
									height: 100%;
									padding: 2px;
									display: flex;
									align-items: center;
									justify-content: center;

									:deep(svg) {
										height: 100%;

										.fill {
											fill: var(--category-color);
										}

										.stroke {
											stroke: var(--category-color);
										}
									}
								}
							}

							.category-items-selected-num {
								@include noSelect;
								width: 24px;
								height: 16px;
								position: absolute;
								top: calc(50% - 8px);
								right: -9px;
								z-index: 0;
								transform: scale(1) skew(-15deg);
								display: flex;
								justify-content: center;
								align-items: center;
								box-shadow: inset 0 1px 2px $alpha_4;
								border-radius: 0 5px 5px 0;
								background: $alpha_1;
								transition: transform 0.4s $transition, right 0.4s $transition;

								&.active {
									right: -34px;
								}

								span {
									font-size: 11px;
								}
							}

							@media(max-width: 1400px) {
								width: 70%;
							}

							@media(max-width: 1100px) {
								width: 80%;
							}

							@media(max-width: 400px) {
								width: 70%;
							}
						}

						.category-actions {
							display: flex;
							align-items: center;
							padding-right: 5px;

							.availability-dot {
								visibility: hidden;
								margin-left: 5px;

								&.visible {
									visibility: visible;
								}
							}

							@media(max-width: 1100px) {
								padding-right: 3px;

								.availability-dot {
									margin-left: 3px;
								}
							}
						}
					}

					.dropdown-arrow {
						width: 30px;
						display: flex;
						justify-content: center;
						align-items: center;
						background-color: var(--color-bg-primary-2);
						position: relative;
						cursor: pointer;
						user-select: none;
						* {
							pointer-events: none;
						}
					}

				}

			}

			:deep(.dropdown-content) {
				margin: 0;
				border-bottom: 1px solid var(--color-border-alpha);
			}


		}

		.category-add {
			position: relative;

			.category-header {
				background-color: var(--color-bg-primary-2);
				padding: 7px 12px;
				display: flex;
				align-items: center;
				cursor: pointer;
			}

			p {
				color: $primary;
				font-weight: 500;
				@include truncateString(1);
				font-size: 14px;
				width: calc(100% - 180px);
				min-width: 120px;
				margin-right: auto;
			}

			&::before {
				content: "";
				position: absolute;
				top: -1px;
				left: 0;
				height: 1px;
				width: 100%;
				background-color: var(--color-alpha-3);
			}

			.list-item-add p {
				width: calc(100% - 30px);
			}
		}

		.category-orphans {

			.handle-placeholder {
				width: 30px;
			}
		}

		@media(max-width: 768px) {
			margin-right: 8px;
		}
		@media(max-width: 400px) {
			.category-container .category-edit-container {
				padding: 0;
			}
		}
	}

}

</style>